import React from "react";

import { Box } from "@material-ui/core";
import { Form, Formik, getIn, setIn, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { Button, SecondaryButton } from "components/Form";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { pluralize } from "lib/pluralize";
import {
  checkboxFieldNameSuffix,
  getCheckboxFieldName,
} from "components/BulkUpdateOptionalFieldsModal/util";
import {
  BulkUpdateFieldsActionsProps,
  BulkUpdateOptionalFieldsModalProps,
  ModalIntroductionProps,
} from "components/BulkUpdateOptionalFieldsModal/types";
import { BulkUpdateFieldsForm } from "components/BulkUpdateOptionalFieldsModal/Form";
import { CombinedMissingFieldIndicator } from "components/MissingFieldsIndicator";
import { Row } from "components/Layout";
import { get, isEmpty, isEqual, set, sortBy, uniq } from "lodash";
import { productFields, productFieldsCamelCase } from "lib/products";
import { selectMasterLabelsBySaleLotId } from "selectors";
import { EMPTY_ARRAY } from "lib";

function BulkUpdateFieldsActions(
  props: BulkUpdateFieldsActionsProps,
): React.JSX.Element {
  const { onClose } = props;
  const { values } = useFormikContext();

  const noCheckboxesSelected = Object.keys(values)
    .filter(fieldName => fieldName.endsWith(checkboxFieldNameSuffix))
    .every(checkboxFieldName => values[checkboxFieldName] !== true);

  const requiredInputFieldNames = ["product_id"];
  const allSelectedAndRequiredInputDone = requiredInputFieldNames.every(
    fieldName =>
      !values[fieldName.concat(checkboxFieldNameSuffix)] || values[fieldName],
  );

  const isDisabled = noCheckboxesSelected || !allSelectedAndRequiredInputDone;
  return (
    <>
      <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      <Button
        data-tour="bulkUpdateFieldsSubmit"
        disabled={isDisabled}
        type="submit"
      >
        Bulk Update
      </Button>
    </>
  );
}

function DefaultModalIntroduction(
  props: ModalIntroductionProps,
): React.JSX.Element {
  const { modelCount } = props;
  return (
    <strong>
      You are bulk editing {modelCount} {pluralize("record", modelCount)}. Any
      changes made to checked fields will be applied to these records.
    </strong>
  );
}

export function BulkUpdateOptionalFieldsModal(
  props: BulkUpdateOptionalFieldsModalProps,
): React.JSX.Element {
  const {
    bulkUpdateActionCreator,
    fields,
    initialValues,
    modelIds,
    modelName,
    modelObjects = {},
    onClose,
    validationSchema,
    ModalIntroduction = DefaultModalIntroduction,
    aggridRef,
    deploymentId,
  } = props;
  const dispatch = useDispatch();
  const masterLabelIdsBySaleLotIdLookup = useSelector(
    selectMasterLabelsBySaleLotId,
  );

  if (!modelIds?.length) {
    return null;
  }

  const prefilledValues = !isEmpty(modelObjects)
    ? fields.reduce((acc, { fieldName, updateFieldNames }) => {
        // if a field has updateFieldNames, we need to use those over the field name,
        // as that is where the sub field values are actually stored.
        // Otherwise we can just use the fieldName to fetch the value.
        const fieldNamesToUpdate = updateFieldNames || [fieldName];

        fieldNamesToUpdate.forEach(field => {
          const uniqueFieldValues = uniq(
            modelIds.map(id => get(modelObjects[id], field)),
          );

          if (uniqueFieldValues.length === 1) {
            set(acc, field, uniqueFieldValues[0]);
          }
        });

        return acc;
      }, {})
    : {};

  const prefilledInitialValues = {
    ...prefilledValues,
    ...initialValues,
  };

  interface UpdatedValues {
    [fieldName: string]: string | string[];
  }

  function getMasterLabelUpdates(
    saleLotId: string,
    updatedValues: UpdatedValues,
  ) {
    const lotMasterLabels = masterLabelIdsBySaleLotIdLookup[saleLotId];
    const currentMasterLabels = lotMasterLabels ? [...lotMasterLabels] : [];
    const newMasterLabels = currentMasterLabels
      .filter(ml => !(updatedValues?.removeLabels || EMPTY_ARRAY).includes(ml))
      .concat(updatedValues?.addLabels || EMPTY_ARRAY);

    if (isEqual(sortBy(currentMasterLabels), sortBy(newMasterLabels))) {
      return undefined; // No updates
    }

    return newMasterLabels;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Formik values are dynamic based on fields prop
  function onSubmit(values: any): void {
    const updatedValues = fields.reduce((acc, field) => {
      const { fieldName, updateFieldNames, emptyValue } = field;

      const isProductField = [
        ...productFields,
        ...productFieldsCamelCase,
      ].includes(fieldName);
      const isProductUpdate = values.product_id_includeInChangeSet;

      const includeInChangeSetFieldName = getCheckboxFieldName(fieldName);
      const shouldIncludeInChangeSet =
        (values[includeInChangeSetFieldName] === true ||
          (isProductField && isProductUpdate)) &&
        fieldName !== "product_id";

      if (shouldIncludeInChangeSet) {
        // Updates the acc with the field values for this field.

        // If the field is a composite field containing multiple fields (eg condition has
        // conditionStore, conditionForward, conditionPrime) we need to
        // iterate through them and update.

        // Note - setIn RETURNS the value, which adds a little obtusse-ness to this stanza.
        if (updateFieldNames) {
          return updateFieldNames.reduce(
            (updatedFieldAcc, updateFieldName) =>
              setIn(
                updatedFieldAcc,
                updateFieldName,
                getIn(values, updateFieldName) || emptyValue,
              ),
            acc,
          );
        }
        // For the simpler use case (eg breed is just breed_id) we can just the fieldName as a simple value.
        return setIn(acc, fieldName, getIn(values, fieldName) || emptyValue);
      }
      return acc;
    }, {});

    const fieldWithBuildPayload = fields.find(f => f.buildPayload);
    const getLabelPayload = fieldWithBuildPayload
      ? (id: string) =>
          fieldWithBuildPayload.buildPayload(
            id,
            updatedValues,
            modelObjects as Record<string, unknown>,
          )
      : (id: string) => ({
          master_labels: getMasterLabelUpdates(id, updatedValues),
        });

    const payloads = modelIds.map(id => ({
      id,
      ...updatedValues,
      removeLabels: undefined,
      addLabels: undefined,
      ...getLabelPayload(id),
    }));
    dispatch(bulkUpdateActionCreator(payloads));
    // deselect selected models from aggrid table
    // if an aggrid ref is passed in - update the aggrid table to deselect selected rows.
    if (aggridRef?.current?.api) {
      aggridRef.current.api.deselectAll();
    }

    onClose();
  }
  const modelCount = modelIds.length;
  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md">
      <Formik
        initialValues={prefilledInitialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <DialogTitle onClose={onClose}>
            <Row justifyBetween alignCenter>
              <div>
                Editing {modelCount} {pluralize(modelName, modelCount)}
              </div>
              <CombinedMissingFieldIndicator modelIds={modelIds} />
            </Row>
          </DialogTitle>
          <DialogContent>
            <Box marginY={1}>
              <ModalIntroduction modelCount={modelCount} />
            </Box>
            <Box padding={2}>
              <BulkUpdateFieldsForm
                fields={fields}
                modelIds={modelIds}
                modelName={modelName}
                deploymentId={deploymentId}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <BulkUpdateFieldsActions onClose={onClose} />
          </DialogActions>
        </Form>
      </Formik>
    </ZoomyDialog>
  );
}
